import { ChangeDetectionStrategy, Component, DestroyRef, afterRenderEffect, booleanAttribute, computed, effect, inject, input, output, signal } from '@angular/core';
import { GoogleMap, MapMarker } from '@angular/google-maps';

import { SearchArea } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { SpinnerComponent } from '../components/common/spinner.component';

/** A branch as a point, reduced to what a pin needs. */
export interface LocationMarker {
  id: string;
  title: string;
  latitude: number;
  longitude: number;
  /** Present but not takeable — shown greyed, because a gap in the pins would read as a gap in town. */
  muted?: boolean;
  /** The partner's own mark, drawn into the pin where given — a brand is read faster than a title. */
  logoUrl?: string;
}

/** Somewhere on the map, as a click leaves it. */
export interface MapPoint {
  latitude: number;
  longitude: number;
}

/** Whether what the map could show is being read as rows or as points. */
export type MapView = 'list' | 'map';

/** The one switch between the two readings, as every picker that carries a map offers it. */
export const MAP_VIEWS: { id: MapView; icon: string; labelKey: string }[] = [
  { id: 'list', icon: 'format_list_bulleted', labelKey: 'guaranteeV2.label.viewList' },
  { id: 'map', icon: 'map', labelKey: 'guaranteeV2.label.viewMap' },
];

interface MarkerView {
  id: string;
  position: google.maps.LatLngLiteral;
  options: google.maps.MarkerOptions;
}

/** Kyiv, where the map opens before anything narrows it. */
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 50.4504, lng: 30.5245 };

const COUNTRY_ZOOM = 6;
const LOCATION_ZOOM = 15;

/** Room for a pin to stand clear of the frame it was fitted into, in pixels on each side. */
const FIT_PADDING = 48;

/**
 * As close as a fit is allowed to land. Two branches a street apart would otherwise frame to a
 * rooftop, which says nothing about where either of them is. Clamped after the fit rather than set
 * as the map's own limit: zooming further in by hand is a reasonable thing to want.
 */
const MAX_FITTED_ZOOM = 16;

const PIN_COLOR = '#214DD9';
const PIN_COLOR_SELECTED = '#00BF73';
const PIN_COLOR_MUTED = '#A3A3A3';
const PIN_WIDTH = 26;
const PIN_WIDTH_SELECTED = 32;

/** How a pin reads: which state colours the ring, the tail and the fallback dot. */
type LogoPinState = 'plain' | 'selected' | 'muted';

/** One branch as the overlay lays it out: where it stands, what it wears, and how it reads. */
interface LogoPinView {
  id: string;
  title: string;
  latitude: number;
  longitude: number;
  logoUrl: string;
  state: LogoPinState;
}

interface LogoPinLayer {
  update(pins: LogoPinView[]): void;
  dispose(): void;
}

const PIN_BUTTON_CLASS = 'absolute flex -translate-x-1/2 -translate-y-full cursor-pointer flex-col items-center border-0 bg-transparent p-0';

const PLATE_CLASS: Record<LogoPinState, string> = {
  plain:
    'relative z-10 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-primary-600 bg-neutral-0 shadow-md transition-all duration-150 motion-reduce:transition-none',
  selected:
    'relative z-10 flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-success-600 bg-neutral-0 shadow-lg transition-all duration-150 motion-reduce:transition-none',
  muted:
    'relative z-10 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-neutral-300 bg-neutral-0 shadow-xs transition-all duration-150 motion-reduce:transition-none',
};

const TAIL_CLASS: Record<LogoPinState, string> = {
  plain: '-mt-1.5 h-2.5 w-2.5 rotate-45 bg-primary-600',
  selected: '-mt-1.5 h-2.5 w-2.5 rotate-45 bg-success-600',
  muted: '-mt-1.5 h-2.5 w-2.5 rotate-45 bg-neutral-300',
};

/** The mark fills the plate and the plate's round edge is the mask: whatever the file's shape, only its centre shows. */
const LOGO_CLASS: Record<LogoPinState, string> = {
  plain: 'h-full w-full object-cover object-center transition-opacity duration-150',
  selected: 'h-full w-full object-cover object-center transition-opacity duration-150',
  muted: 'h-full w-full object-cover object-center grayscale transition-opacity duration-150',
};

/** What stands in for a mark that never arrived: the plate stays, dotted like a plain pin's eye. */
const DOT_CLASS: Record<LogoPinState, string> = {
  plain: 'h-2.5 w-2.5 rounded-full bg-primary-600',
  selected: 'h-3 w-3 rounded-full bg-success-600',
  muted: 'h-2.5 w-2.5 rounded-full bg-neutral-300',
};

const PIN_Z: Record<LogoPinState, string> = { muted: '10', plain: '20', selected: '30' };

function pinStateOf(marker: LocationMarker, selectedId: string | undefined): LogoPinState {
  if (marker.id === selectedId) {
    return 'selected';
  }

  return marker.muted ? 'muted' : 'plain';
}

/**
 * The branded pins, as real elements laid over the map.
 *
 * A marker icon is one flat image, and the marks live on a host that answers no cross-origin
 * reads, so no icon can be composed out of them on our side — and the icon scaling the api does
 * offer treats formats unevenly. Elements need none of that: the plate is drawn here, the mark
 * sits on it as an ordinary image fitted by css, and a mark that is slow or missing leaves a
 * plate that already stands, still says its title and still takes the click.
 *
 * Made through a factory because the class extends a google type, and there is no google until
 * the script has landed.
 */
function createLogoPinLayer(map: google.maps.Map, picked: (id: string) => void): LogoPinLayer {
  class Layer extends google.maps.OverlayView implements LogoPinLayer {
    private readonly pins = new Map<string, { root: HTMLButtonElement; at: google.maps.LatLng }>();
    private readonly holder = document.createElement('div');

    override onAdd(): void {
      this.getPanes()?.overlayMouseTarget.appendChild(this.holder);
    }

    override onRemove(): void {
      this.holder.remove();
    }

    /** Standing pixels are re-derived from the standing coordinates whenever the projection moves. */
    override draw(): void {
      const projection = this.getProjection();

      if (!projection) {
        return;
      }

      for (const pin of this.pins.values()) {
        const point = projection.fromLatLngToDivPixel(pin.at);

        if (point) {
          pin.root.style.left = `${point.x}px`;
          pin.root.style.top = `${point.y}px`;
        }
      }
    }

    /** Nodes are kept by id and redressed in place, so a state change never re-fetches a mark. */
    update(views: LogoPinView[]): void {
      const alive = new Set(views.map(view => view.id));

      for (const [id, pin] of this.pins) {
        if (!alive.has(id)) {
          pin.root.remove();
          this.pins.delete(id);
        }
      }

      for (const view of views) {
        const pin = this.pins.get(view.id) ?? this.place(view.id);

        pin.at = new google.maps.LatLng(view.latitude, view.longitude);
        this.dress(pin.root, view);
      }

      this.draw();
    }

    dispose(): void {
      this.setMap(null);
    }

    private place(id: string): { root: HTMLButtonElement; at: google.maps.LatLng } {
      const root = document.createElement('button');
      root.type = 'button';
      root.className = PIN_BUTTON_CLASS;

      const plate = document.createElement('div');
      const image = document.createElement('img');
      const dot = document.createElement('span');

      image.alt = '';
      image.style.opacity = '0';
      dot.hidden = true;

      // The mark fades in once it has pixels, and a mark that never gets any concedes to the dot.
      image.addEventListener('load', () => {
        image.style.opacity = root.dataset['logoOpacity'] ?? '1';
      });
      image.addEventListener('error', () => {
        image.hidden = true;
        dot.hidden = false;
      });

      plate.append(image, dot);

      const tail = document.createElement('div');
      root.append(plate, tail);

      // The map underneath pans on press and, when pickable, places the centre on click; a press
      // on a pin is neither, and is kept to the pin.
      for (const type of ['pointerdown', 'mousedown', 'touchstart', 'dblclick'] as const) {
        root.addEventListener(type, event => event.stopPropagation());
      }

      root.addEventListener('click', event => {
        event.stopPropagation();
        picked(id);
      });

      this.holder.appendChild(root);

      const pin = { root, at: new google.maps.LatLng(0, 0) };
      this.pins.set(id, pin);

      return pin;
    }

    private dress(root: HTMLButtonElement, view: LogoPinView): void {
      root.title = view.title;
      root.setAttribute('aria-label', view.title);
      root.style.zIndex = PIN_Z[view.state];
      root.dataset['logoOpacity'] = view.state === 'muted' ? '0.4' : '1';

      const plate = root.children[0] as HTMLElement;
      const tail = root.children[1] as HTMLElement;
      const image = plate.children[0] as HTMLImageElement;
      const dot = plate.children[1] as HTMLElement;

      plate.className = PLATE_CLASS[view.state];
      tail.className = TAIL_CLASS[view.state];
      image.className = LOGO_CLASS[view.state];
      dot.className = DOT_CLASS[view.state];

      if (image.getAttribute('src') !== view.logoUrl) {
        image.hidden = false;
        dot.hidden = true;
        image.style.opacity = '0';
        image.src = view.logoUrl;
      } else if (!image.hidden && image.complete) {
        image.style.opacity = root.dataset['logoOpacity'] ?? '1';
      }
    }
  }

  const layer = new Layer();
  layer.setMap(map);

  return layer;
}

/** The area searched, drawn faintly enough that the pins standing in it stay the subject. */
const AREA_STYLE = { strokeColor: '#214DD9', strokeOpacity: 0.5, strokeWeight: 1, fillColor: '#214DD9', fillOpacity: 0.06, clickable: false } as const;

/**
 * A teardrop in the palette the rest of the module uses, as a data uri. Drawn rather than fetched:
 * the stock map pins are a different design language, and a marker image is one more request that
 * can fail behind a proxy and leave the map with nothing on it.
 */
function pin(fill: string, width: number): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${Math.round((width * 4) / 3)}" viewBox="0 0 24 32">` +
    '<path d="M12 0.75C5.787 0.75 0.75 5.787 0.75 12c0 4.03 2.63 8.63 5.28 12.2A62 62 0 0 0 12 31.1a62 62 0 0 0 5.97-6.9c2.65-3.57 5.28-8.17 5.28-12.2C23.25 5.787 18.213 0.75 12 0.75Z" ' +
    `fill="${fill}" stroke="#FFFFFF" stroke-width="1.5"/>` +
    '<circle cx="12" cy="12" r="4.25" fill="#FFFFFF"/>' +
    '</svg>';

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const CENTRE_DOT_WIDTH = 14;

/** The point everything was measured from, drawn as a ringed dot so it is not mistaken for a pin. */
function dot(): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${CENTRE_DOT_WIDTH}" height="${CENTRE_DOT_WIDTH}" viewBox="0 0 14 14">` +
    `<circle cx="7" cy="7" r="6" fill="#FFFFFF" stroke="${PIN_COLOR}" stroke-width="2"/>` +
    `<circle cx="7" cy="7" r="2.5" fill="${PIN_COLOR}"/>` +
    '</svg>';

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/** Quiet enough for the pins to be the only thing worth reading. */
const MAP_OPTIONS: google.maps.MapOptions = {
  center: DEFAULT_CENTER,
  zoom: COUNTRY_ZOOM,
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  // The map sits inside a scrolling dialog, so a wheel over it scrolls the dialog as it would
  // anywhere else, and zooming is asked for with a modifier.
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#656566' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#FAFAFA' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#DCF0FF' }] },
  ],
};

/**
 * The same locations a list would show, as points.
 *
 * Where the branch is matters to a manager who knows the city and not the street names, and an
 * address alone does not say which of four branches is the one across from the member's home. The
 * view frames itself: on the chosen point when there is one, on the spread of what is offered
 * otherwise, and a control puts the whole spread back after a choice has narrowed it.
 *
 * The framing is asked of the map rather than computed: a projection worked out here would have to
 * know the width the map was given, and it is given whatever the dialog has left.
 */
@Component({
  selector: 'guarantees-location-map',
  standalone: true,
  imports: [GoogleMap, MapMarker, MatIcon, MatTooltip, SpinnerComponent, TranslatePipe],
  host: { class: 'relative block overflow-hidden rounded-lg border border-neutral-100', '[style.display]': "shown() ? null : 'none'" },
  template: `
    <google-map class="block h-full w-full" height="100%" width="100%" [options]="options()" (mapInitialized)="onReady($event)">
      @for (marker of markerViews(); track marker.id) {
        <map-marker [position]="marker.position" [options]="marker.options" (mapClick)="selected.emit(marker.id)"></map-marker>
      }

      <!-- Where the search was run from, so a branch on the edge of the circle is read as such. -->
      @if (centreView(); as centre) {
        <map-marker [position]="centre.position" [options]="centre.options"></map-marker>
      }
    </google-map>

    <!-- How much is on the map, and which pin is the answer — read without hovering every one.
         While an answer is in flight the count would be a count of nothing, so the flight is said instead. -->
    <span class="pointer-events-none absolute top-2 left-2 flex max-w-[calc(100%-5.5rem)] flex-col items-start gap-1">
      @if (busy()) {
        <span class="flex items-center gap-1.5 rounded-full bg-neutral-0/95 px-2.5 py-1 text-xs font-semibold text-neutral-600 shadow-sm">
          <guarantees-spinner></guarantees-spinner>
          {{ busyLabel() }}
        </span>
      } @else {
        <span class="rounded-full bg-neutral-0/90 px-2 py-0.5 text-xs font-semibold text-neutral-600 shadow-xs">
          {{ 'guaranteeV2.label.mappedLocations' | translate: { count: markers().length } }}
        </span>

        @if (named()) {
          @if (chosen(); as pick) {
            <span class="flex max-w-full items-center gap-1 rounded-full bg-success-600 px-2 py-0.5 text-xs font-semibold text-neutral-0 shadow-xs">
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">check_circle</mat-icon>
              <span class="min-w-0 truncate">{{ pick.title }}</span>
            </span>
          }
        }
      }
    </span>

    <!-- Puts everything the search left standing back in view after a choice has closed in on one.
         Nothing the automatic framing watches changes here, so the spread stays until another pin is taken. -->
    @if (chosen() && markers().length > 1) {
      <button
        class="absolute top-2 right-2 inline-flex items-center justify-center rounded-full bg-neutral-0/90 p-1.5 text-neutral-600 shadow-xs transition-colors duration-150 hover:bg-neutral-0 hover:text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
        type="button"
        [attr.aria-label]="'guaranteeV2.action.showAllLocations' | translate"
        [matTooltip]="'guaranteeV2.action.showAllLocations' | translate"
        (click)="fitAll()">
        <mat-icon class="h-4! w-4! text-base! leading-4!">zoom_out_map</mat-icon>
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationMapComponent {
  markers = input.required<LocationMarker[]>();
  selectedId = input<string | undefined>(undefined);
  /** The area the list was searched over. Unset where the list was not narrowed by distance. */
  centre = input<SearchArea | undefined>(undefined);
  /** Turns the map into the answer rather than the illustration: a click on it places the centre. */
  pickable = input(false, { transform: booleanAttribute });
  /**
   * Hidden rather than destroyed while the list has the floor: the map instance, its tiles and the
   * hand-chosen framing all survive the switch, so coming back to it costs nothing and loses nothing.
   */
  shown = input(true, { transform: booleanAttribute });
  /**
   * Where to stand before anything is chosen: framed to this place at this radius, drawing nothing.
   * A picking map opened over a whole country makes the first click a guess; opened over the
   * member's own streets at the scale the search will run at, it is the question already half asked.
   */
  preview = input<SearchArea | undefined>(undefined);
  /** An answer is in flight, said on the map itself — the pins it will replace are already gone. */
  busy = input(false, { transform: booleanAttribute });
  busyLabel = input<string>('');
  /**
   * Whether the chosen pin is captioned in the corner. Off where the caller lays a card of its own
   * over the map, which would otherwise name the same place twice.
   */
  named = input(true, { transform: booleanAttribute });

  selected = output<string>();
  picked = output<MapPoint>();

  /**
   * A map being picked on says so with the pointer. Bound rather than fixed: the same component is
   * a read-only map everywhere else, and a crosshair over one invites a click that does nothing.
   */
  protected readonly options = computed<google.maps.MapOptions>(() => (this.pickable() ? { ...MAP_OPTIONS, draggableCursor: 'crosshair' } : MAP_OPTIONS));

  /** The map itself, once the api has handed one over. Nothing here is framed before that. */
  private map?: google.maps.Map;

  /** The area drawn on the map, kept and re-set rather than replaced so it does not flicker. */
  private area?: google.maps.Circle;

  /** The radius the picking map was last fitted to, so only a changed one re-frames it. */
  private fittedRadius?: number;

  /** The centre the picking map last stood on, so a pin taken at the edge does not yank it back. */
  private framedCentre?: string;

  private readonly ready = signal(false);

  /** The branded pins, living as elements over the map rather than as marker icons. */
  private pinLayer?: LogoPinLayer;

  protected readonly chosen = computed(() => this.markers().find(marker => marker.id === this.selectedId()));

  /**
   * The centre as a pin of its own — a hollow dot rather than a teardrop, so it never reads as one
   * more branch to choose between.
   */
  protected readonly centreView = computed<MarkerView | undefined>(() => {
    const centre = this.centre();

    if (!this.ready() || !centre) {
      return undefined;
    }

    return {
      id: 'centre',
      position: { lat: centre.latitude, lng: centre.longitude },
      options: {
        title: centre.label,
        icon: {
          url: dot(),
          scaledSize: new google.maps.Size(CENTRE_DOT_WIDTH, CENTRE_DOT_WIDTH),
          anchor: new google.maps.Point(CENTRE_DOT_WIDTH / 2, CENTRE_DOT_WIDTH / 2),
        },
        optimized: false,
        clickable: false,
        zIndex: 0,
      },
    };
  });

  /**
   * Built only once the api is in hand: an icon states its anchor as a `google.maps.Point`, which
   * is a constructor and not a shape that can be written down ahead of it loading.
   */
  protected readonly markerViews = computed<MarkerView[]>(() => {
    if (!this.ready()) {
      return [];
    }

    const selectedId = this.selectedId();

    // Branches wearing a mark stand as overlay plates instead; only the unmarked take a teardrop.
    return this.markers()
      .filter(marker => !marker.logoUrl)
      .map(marker => {
        const isSelected = marker.id === selectedId;
        const width = isSelected ? PIN_WIDTH_SELECTED : PIN_WIDTH;
        const height = Math.round((width * 4) / 3);

        return {
          id: marker.id,
          position: { lat: marker.latitude, lng: marker.longitude },
          options: {
            title: marker.title,
            // The tip of the pin is what stands on the coordinate, not the middle of the image.
            icon: {
              url: pin(isSelected ? PIN_COLOR_SELECTED : marker.muted ? PIN_COLOR_MUTED : PIN_COLOR, width),
              scaledSize: new google.maps.Size(width, height),
              anchor: new google.maps.Point(width / 2, height),
            },
            // Rasterising into one canvas drops the z-order these rely on to keep the answer on top.
            optimized: false,
            zIndex: isSelected ? 3 : marker.muted ? 1 : 2,
          },
        };
      });
  });

  constructor() {
    // The overlay follows the same state the plain pins read, one redress per change.
    effect(() => {
      if (!this.ready()) {
        return;
      }

      const selectedId = this.selectedId();

      this.pinLayer?.update(
        this.markers()
          .filter(marker => marker.logoUrl)
          .map(marker => ({
            id: marker.id,
            title: marker.title,
            latitude: marker.latitude,
            longitude: marker.longitude,
            logoUrl: marker.logoUrl!,
            state: pinStateOf(marker, selectedId),
          }))
      );
    });

    // Reframed on every change to what is offered or what was taken, which is the only thing that
    // moves the view on its own — a pan or a zoom by hand is left alone until one of those happens.
    // After render, because a fit is a measurement: a container still hidden or still being laid
    // out measures zero, and a fit onto nothing lands the view nowhere.
    afterRenderEffect(() => {
      const shown = this.shown();

      this.markers();
      this.selectedId();
      this.centre();
      this.preview();
      this.ready();

      if (!shown) {
        return;
      }

      this.paintArea();
      this.frame();
    });

    inject(DestroyRef).onDestroy(() => {
      this.area?.setMap(null);
      this.pinLayer?.dispose();
    });
  }

  protected onReady(map: google.maps.Map): void {
    this.map = map;
    this.pinLayer = createLogoPinLayer(map, id => this.selected.emit(id));
    this.ready.set(true);

    map.addListener('click', (event: google.maps.MapMouseEvent) => {
      const at = event.latLng;

      if (this.pickable() && at) {
        this.picked.emit({ latitude: at.lat(), longitude: at.lng() });
      }
    });
  }

  /** The circle the search was run over, laid under the pins so the radius is a thing one can see. */
  private paintArea(): void {
    const map = this.map;
    if (!map) {
      return;
    }

    const centre = this.centre();
    if (!centre) {
      this.area?.setMap(null);

      return;
    }

    const options = { ...AREA_STYLE, center: { lat: centre.latitude, lng: centre.longitude }, radius: centre.radiusMeters, map };

    this.area ??= new google.maps.Circle();
    this.area.setOptions(options);
  }

  private frame(): void {
    const map = this.map;
    if (!map) {
      return;
    }

    // A map being picked on is driven by hand. It is fitted once, so the radius can be seen against
    // the streets, and after that a new point only pans — re-zooming under a clicking hand fights
    // it. Before anything is chosen it stands over the preview, framed to the same scale, so the
    // first click is placed on streets rather than on a country.
    if (this.pickable()) {
      const centre = this.centre();
      const stand = centre ?? this.preview();

      if (!stand) {
        return;
      }

      const at = `${stand.latitude}:${stand.longitude}`;

      if (this.fittedRadius !== stand.radiusMeters) {
        this.fittedRadius = stand.radiusMeters;
        this.fitCircle(stand);
      } else if (centre && at !== this.framedCentre) {
        map.setCenter({ lat: centre.latitude, lng: centre.longitude });
      }

      this.framedCentre = at;

      return;
    }

    const chosen = this.chosen();
    if (chosen) {
      map.setCenter({ lat: chosen.latitude, lng: chosen.longitude });
      map.setZoom(LOCATION_ZOOM);

      return;
    }

    this.fitAll();
  }

  /**
   * Framed to the circle whether or not one is drawn: what a preview and a searched area share is
   * the scale, and the scale is the whole point of fitting either.
   */
  private fitCircle(centre: SearchArea): void {
    const bounds = new google.maps.Circle({ center: { lat: centre.latitude, lng: centre.longitude }, radius: centre.radiusMeters }).getBounds();

    if (bounds) {
      this.map?.fitBounds(bounds, FIT_PADDING);
    }
  }

  protected fitAll(): void {
    const map = this.map;
    if (!map) {
      return;
    }

    // The area is part of what is being shown, so it is framed even where nothing was found in it:
    // an empty circle says the search was run somewhere, where an empty country says nothing.
    const searched = this.area?.getMap() ? this.area.getBounds() : undefined;

    const markers = this.markers();
    if (markers.length === 0) {
      if (searched) {
        map.fitBounds(searched, FIT_PADDING);

        return;
      }

      map.setCenter(DEFAULT_CENTER);
      map.setZoom(COUNTRY_ZOOM);

      return;
    }

    if (markers.length === 1 && !searched) {
      map.setCenter({ lat: markers[0].latitude, lng: markers[0].longitude });
      map.setZoom(LOCATION_ZOOM);

      return;
    }

    const bounds = searched ? new google.maps.LatLngBounds(searched.getSouthWest(), searched.getNorthEast()) : new google.maps.LatLngBounds();
    for (const marker of markers) {
      bounds.extend({ lat: marker.latitude, lng: marker.longitude });
    }

    map.fitBounds(bounds, FIT_PADDING);

    // A fit onto points barely apart lands far closer than the spread deserves, and the zoom it
    // settled on is only known once it has settled.
    google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > MAX_FITTED_ZOOM) {
        map.setZoom(MAX_FITTED_ZOOM);
      }
    });
  }
}
